/// <amd-module name="Wit/Common/QueryListServices/QueryListTreeItemProvider" />
import { ITreeItem, ITreeItemAdd, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { EmptyItem, QueryItem } from "Wit/Common/QueryUtils/Models";

export class QueryListTreeItemProvider extends TreeItemProvider<QueryItem | EmptyItem> {
    constructor(rootItems: ITreeItem<QueryItem | EmptyItem>[] = []) {
        super(rootItems);
    }

    public override splice(
        parentItem: ITreeItem<QueryItem | EmptyItem> | undefined,
        itemsToRemove?: ITreeItem<QueryItem | EmptyItem>[],
        itemsToAdd?: ITreeItemAdd<QueryItem | EmptyItem>[]
    ): void {
        let parentItemEx: ITreeItemEx<QueryItem | EmptyItem> | undefined;
        let notifyChange = false;
        let parentIndex = -1;
        let childItems: ITreeItem<QueryItem | EmptyItem>[] = super.roots;

        if (parentItem) {
            const keys = this.itemMap.keys();
            for (const key of keys) {
                if (!key.data.isEmptyFolderContext && !parentItem.data.isEmptyFolderContext && key.data.id === parentItem.data.id) {
                    parentItemEx = this.itemMap.get(key);
                    break;
                }
            }
            if (!parentItemEx) {
                return;
            }

            // Find the location in the table items we need splice changes.
            parentIndex = this.indexOf(parentItem);
            childItems = parentItemEx.underlyingItem.childItems || [];
        }

        // Before making any changes make a copy of the removed array, this allows
        // the caller to supply the current childItems to clear them.
        if (itemsToRemove) {
            itemsToRemove = itemsToRemove.slice(0);
        }

        // We will process the adds before the removes.
        if (itemsToAdd) {
            for (let index = 0; index < itemsToAdd.length; index++) {
                const tableItems =
                    !parentItem || (parentIndex >= 0 && parentItem.expanded) ? ([] as ITreeItemEx<QueryItem | EmptyItem>[]) : undefined;
                const itemToAdd = itemsToAdd[index];
                let insertIndex = parentIndex;
                let childIndex = 0;

                // If we are inserting after a specific item find that item. If it
                // isn't found, ignore the add.
                if (itemToAdd.insertAfter) {
                    // Ensure the insertAfter is a child of the parentItem.
                    if ((childIndex = childItems.indexOf(itemToAdd.insertAfter)) === -1) {
                        continue;
                    }

                    // Find the location in the underlying tableItems to insert.
                    const childInsertIndex = this.indexOf(itemToAdd.insertAfter, parentIndex + 1);
                    if (childInsertIndex !== -1) {
                        // Determine the number of elements in table under the insertAfter, we need to insert
                        // after these elements to ensure the inserted element is a sibling of insertAfter.
                        insertIndex = childInsertIndex + this.getTableChildCount(itemToAdd.insertAfter);
                    }
                } else {
                    // if we add to the front, childIndex needs to be -1 so when we do the +1 later, it will be 0
                    childIndex--;
                }

                // Add the items to the overall item map and compute the item being sent to the table.
                this.addItems(itemToAdd.items, parentItemEx, tableItems);

                // Update the childItems and if this is the first child update the treeItem.
                // We will notify if we are adding the first child since the state of the item is changing.
                childItems.splice(childIndex + 1, 0, ...itemToAdd.items);
                if (parentItemEx && !parentItemEx.underlyingItem.childItems) {
                    parentItemEx.underlyingItem.childItems = childItems;
                    notifyChange = true;
                }

                // Add the new items to the set of table items.
                if (tableItems) {
                    this.tableItems.splice(insertIndex + 1, 0, ...tableItems);
                    notifyChange = true;
                }
            }
        }

        // Now process the removes.
        if (itemsToRemove) {
            for (let index = 0; index < itemsToRemove.length; index++) {
                const itemToRemove = itemsToRemove[index];
                const tableItems = !parentItem || parentItem.expanded ? ([] as ITreeItem<QueryItem | EmptyItem>[]) : undefined;
                let childIndex = childItems.indexOf(itemToRemove);

                //Additionally check for an empty folder marker when there is more than 1 child
                if (childIndex === -1 && childItems.length > 1 && childItems.some(item => item.data.isEmptyFolderContext) && itemsToRemove?.some(item => item.data.isEmptyFolderContext)) {
                    childIndex = childItems.findIndex(item => item.data.isEmptyFolderContext);
                }

                // Ensure the itemToRemove is a child of the parentItem.
                if (childIndex === -1) {
                    continue;
                }

                // Remove the items from the underlying map and compute the set of items in the table.
                this.removeItem(itemToRemove, tableItems);

                // Remove the item from this childItem list. Notify if we removed the last child
                // since this is changing the state of the item.
                childItems.splice(childIndex, 1);
                if (childItems.length === 0 && parentItem) {
                    delete parentItem.childItems;
                    notifyChange = true;
                }

                const removeIndex = this.indexOf(itemToRemove, parentIndex + 1);
                if (removeIndex === -1) {
                    continue;
                }

                // Remove the items from the underlying observable.
                if (tableItems) {
                    this.tableItems.splice(removeIndex, tableItems.length);
                    notifyChange = true;
                }
            }
        }

        if (notifyChange && parentItemEx && parentIndex !== -1) {
            this.tableItems.change(parentIndex, parentItemEx);
        }
    }
}